// src/core/common/dataTable/index.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table } from "antd";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable, defaultAnimateLayoutChanges } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DatatableProps } from "../../data/interface";
import ResizableColumnHeader from "./ResizableColumnHeader";

type RowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  "data-row-key": string | number;
};

// Smooth layout animation (prevents height collapse)
const animateLayoutChanges = (args: any) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });


interface SortableRowProps extends RowProps {
  CustomRow?: React.ComponentType<any>;
}

function SortableRow({ CustomRow, ...props }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: props["data-row-key"],
    animateLayoutChanges,
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    // Smooth transition for both transform and opacity
    transition:
      "transform 0.22s cubic-bezier(0.2, 0, 0, 1), opacity 0.22s ease",
    opacity: isDragging ? 0.7 : 1,
    cursor: isDragging ? "move" : "move",
    // Optional: subtle highlight when dragging over
    backgroundColor: isOver ? "rgba(0, 123, 255, 0.05)" : undefined,
    // Shadow during drag
    boxShadow: isDragging
      ? "0 8px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)"
      : undefined,
    zIndex: isDragging ? 999 : "auto",
  };

  if (CustomRow) {
     return (
         <CustomRow 
            {...props}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
         />
     )
  }

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
}

// Header cell wrapper that always applies background styling
const HeaderCellWithBackground = ({
  style,
  className,
  children,
  column,
  onResize,
  columnWidths,
  ...props
}: any) => {
  // Check if this is a sticky column (fixed left/right) or selection column
  const isSelectionColumn = props.className?.includes('ant-table-selection-column');
  const isSticky = props.className?.includes('ant-table-cell-fix-left') ||
    props.className?.includes('ant-table-cell-fix-right') ||
    isSelectionColumn;

  const columnKey = column?.key || column?.dataIndex;
  // Only use columnWidths if it exists, otherwise keep original width from column or style
  const hasResizedWidth = columnWidths && columnKey && columnWidths[columnKey];
  const currentWidth = hasResizedWidth ? columnWidths[columnKey] : (column?.width || style?.width);

  const combinedStyle: React.CSSProperties = {
    ...(style || {}),
    backgroundColor: (style && style.backgroundColor) || "#fff", // Always apply white background
    // Only set width if we have a resized width or explicit column width
    ...(hasResizedWidth && { width: `${currentWidth}px` }),
    ...(column?.width && !hasResizedWidth && { width: typeof column.width === 'number' ? `${column.width}px` : column.width }),
    // For sticky columns, ensure they stay on top and cover scrolling content
    ...(isSticky && {
      position: "sticky",
      // Selection column needs highest z-index to cover all scrolling headers
      zIndex: isSelectionColumn ? 10 : 3,
      background: "#fff !important", // Force white background
      backgroundColor: "#fff !important", // Ensure background color
    }),
    // For non-sticky columns, ensure they have lower z-index
    ...(!isSticky && {
      zIndex: 1,
    }),
  };

  // Don't add resizer to selection column
  if (isSelectionColumn || !onResize || !columnKey) {
    const CustomCell = props.CustomCell;
    if (CustomCell) {
         return (
             <CustomCell {...props} style={combinedStyle} className={className}>
                 {children}
             </CustomCell>
         )
    }
    return (
      <th
        {...props}
        style={combinedStyle}
        className={className}
      >
        {children}
      </th>
    );
  }

  const CustomCell = props.CustomCell;
  if (CustomCell) {
      return (
        <CustomCell 
             {...props}
             style={combinedStyle}
             className={className}
             ref={(el: any) => {
                if (el && onResize) {
                (el as any).__columnKey = columnKey;
                }
            }}
        >
             <ResizableColumnHeader
                onResize={(newWidth) => onResize(columnKey, newWidth)}
                minWidth={50}
                maxWidth={1000}
                style={{ width: '100%', height: '100%' }}
            >
                {children}
            </ResizableColumnHeader>
        </CustomCell>
      )
  }

  return (
    <th
      {...props}
      style={combinedStyle}
      className={className}
      ref={(el) => {
        if (el && onResize) {
          // Store reference for getting actual width
          (el as any).__columnKey = columnKey;
        }
      }}
    >
      <ResizableColumnHeader
        onResize={(newWidth) => onResize(columnKey, newWidth)}
        minWidth={50}
        maxWidth={1000}
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </ResizableColumnHeader>
    </th>
  );
};

const DraggableHeaderCell = ({
  style,
  className,
  children,
  column,
  onResize,
  columnWidths,
  ...props
}: any) => {
  // 🔍 Detect checkbox column
  const isSelectionColumn =
    className?.includes("ant-table-selection-column");

  const isSticky =
    isSelectionColumn ||
    props.column?.fixed === "left" ||
    props.column?.fixed === "right" ||
    className?.includes("ant-table-cell-fix-left") ||
    className?.includes("ant-table-cell-fix-right");

  // ✅ Hook is ALWAYS called (rule-safe)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.id ?? "__selection__", // safe fallback
    disabled: isSticky,              // 🔥 disable drag for checkbox & sticky
  });

  const columnKey = column?.key || column?.dataIndex;
  // Only use columnWidths if it exists, otherwise keep original width from column or style
  const hasResizedWidth = columnWidths && columnKey && columnWidths[columnKey];
  const currentWidth = hasResizedWidth ? columnWidths[columnKey] : (column?.width || style?.width);

  const combinedStyle: React.CSSProperties = {
    ...(style || {}),
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isSticky ? "default" : "move",
    zIndex: isDragging ? 15 : isSticky ? 3 : 1,
    backgroundColor: "#fff",
    opacity: isDragging ? 0.3 : 1,
    // Only set width if we have a resized width or explicit column width
    ...(hasResizedWidth && { width: `${currentWidth}px` }),
    ...(column?.width && !hasResizedWidth && { width: typeof column.width === 'number' ? `${column.width}px` : column.width }),
  };

  const CustomCell = props.CustomCell;

  // Don't add resizer to selection column
  if (isSelectionColumn || !onResize || !columnKey) {
    if (CustomCell) {
        return (
             <CustomCell 
                {...props}
                ref={setNodeRef}
                style={combinedStyle}
                className={className}
                {...(!isSticky ? attributes : {})}
                {...(!isSticky ? listeners : {})}
             >
                 {children}
             </CustomCell>
        )
    }
    return (
      <th
        {...props}
        ref={setNodeRef}
        style={combinedStyle}
        className={className}
        {...(!isSticky ? attributes : {})}
        {...(!isSticky ? listeners : {})}
      >
        {children}
      </th>
    );
  }

  if (CustomCell) {
    return (
        <CustomCell
            {...props}
            ref={setNodeRef}
            style={combinedStyle}
            className={className}
            {...(!isSticky ? attributes : {})}
            {...(!isSticky ? listeners : {})}
        >
             <ResizableColumnHeader
                onResize={(newWidth) => onResize(columnKey, newWidth)}
                minWidth={50}
                maxWidth={1000}
                style={{ width: '100%', height: '100%' }}
            >
                {children}
            </ResizableColumnHeader>
        </CustomCell>
    )
  }

  return (
    <th
      {...props}
      ref={setNodeRef}
      style={combinedStyle}
      className={className}
      {...(!isSticky ? attributes : {})}
      {...(!isSticky ? listeners : {})}
    >
      <ResizableColumnHeader
        onResize={(newWidth) => onResize(columnKey, newWidth)}
        minWidth={50}
        maxWidth={1000}
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </ResizableColumnHeader>
    </th>
  );
};


/* ------------------------------------------------------------------ */
/*                         MAIN COMPONENT                             */
/* ------------------------------------------------------------------ */
const Datatable: React.FC<
  DatatableProps & {
    loading?: boolean;
    draggable?: boolean;
    enableColumnDrag?: boolean;
    enableColumnResize?: boolean;
    enableRowSelection?: boolean;
    columnWidthStorageKey?: string;
    recordLabel?: string;
    emptyText?: string;
    emptyIcon?: string;
    onRowOrderChange?: (newData: any[]) => void;
    onColumnOrderChange?: (newColumns: any[]) => void;
    onColumnResize?: (columnKey: string, width: number) => void;
  }
> = ({
  columns,
  dataSource: externalDataSource,
  rowKey = "id",
  enableRowSelection = false,
  rowSelection: externalRowSelection,
  loading,
  emptyText = "No data available",
  emptyIcon = "ti ti-database-off",
  recordLabel = "records",
  rowClassName,
  className,
  pagination,
  scroll,
  components,
  expandable,
  draggable = false,
  enableColumnDrag = false,
  enableColumnResize = true,
  columnWidthStorageKey,
  onRowOrderChange,
  onColumnOrderChange,
  onColumnResize,
  ...rest
}) => {
    const [orderedData, setOrderedData] = useState<any[]>(externalDataSource);
    const [orderedColumns, setOrderedColumns] = useState<any[]>(columns);
    const [activeDragItemId, setActiveDragItemId] = useState<any>(null);

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
      // Try to load from localStorage if storage key is provided
      if (columnWidthStorageKey) {
        try {
          const saved = localStorage.getItem(columnWidthStorageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (typeof parsed === 'object' && parsed !== null) {
              return parsed;
            }
          }
        } catch (e) {
          console.warn('Failed to load column widths from localStorage', e);
        }
      }

      // Don't initialize with default widths - let Ant Design handle sizing
      // Only track widths that have been explicitly resized by user
      return {};
    });
    // Current:
    const [pageSizeInput, setPageSizeInput] = useState<number | undefined>(
      typeof pagination === "object" && pagination.pageSize
        ? pagination.pageSize
        : 10
    );

    useEffect(() => {
      if (pagination && typeof pagination === "object" && pagination.pageSize) {
        setPageSizeInput(pagination.pageSize);
      }
    }, [pagination]);


    useEffect(() => {
      setOrderedData(externalDataSource);
    }, [externalDataSource]);

    useEffect(() => {
      setOrderedColumns(columns);
      // Don't initialize widths - let Ant Design handle default sizing
      // Only track widths that have been explicitly resized
    }, [columns]);

    const dataToRender = draggable ? orderedData : externalDataSource;

    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
    const internalRowSelection = enableRowSelection
  ? {
      selectedRowKeys,
      onChange: (keys: any[]) => setSelectedRowKeys(keys),
    }
  : undefined;

  const finalRowSelection =
  enableRowSelection
    ? externalRowSelection ?? internalRowSelection
    : undefined;


    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const getRowId = (record: any) => {
      if (typeof rowKey === "function") {
        return (rowKey as any)(record);
      }
      return (record as any)[rowKey as any];
    };

    const handleDragStart = (event: any) => {
      setActiveDragItemId(event.active.id);
    };

    const handleDragCancel = () => {
      setActiveDragItemId(null);
    };

    const handleDragEnd = (event: any) => {
      const { active, over } = event;
      setActiveDragItemId(null);

      if (!over || active.id === over.id) return;

      // Check if dragging a row
      const isRowDrag =
        draggable &&
        dataToRender.some((i) => getRowId(i) === active.id) &&
        dataToRender.some((i) => getRowId(i) === over.id);

      if (isRowDrag) {
        const oldIdx = dataToRender.findIndex((i) => getRowId(i) === active.id);
        const newIdx = dataToRender.findIndex((i) => getRowId(i) === over.id);

        const newOrdered = arrayMove(dataToRender, oldIdx, newIdx);
        setOrderedData(newOrdered);
        onRowOrderChange?.(newOrdered);
        return;
      }

      // Check if dragging a column
      const isColumnDrag =
        enableColumnDrag &&
        orderedColumns.some((c) => (c.key || c.dataIndex) === active.id) &&
        orderedColumns.some((c) => (c.key || c.dataIndex) === over.id);

      if (isColumnDrag) {
        const oldCol = orderedColumns.find(
          (c) => (c.key || c.dataIndex) === active.id
        );
        const newCol = orderedColumns.find(
          (c) => (c.key || c.dataIndex) === over.id
        );

        // Prevent dragging if either column is sticky
        if (oldCol?.fixed || newCol?.fixed) {
          return;
        }

        const oldIdx = orderedColumns.findIndex(
          (c) => (c.key || c.dataIndex) === active.id
        );
        const newIdx = orderedColumns.findIndex(
          (c) => (c.key || c.dataIndex) === over.id
        );

        const newOrdered = arrayMove(orderedColumns, oldIdx, newIdx);
        setOrderedColumns(newOrdered);
        onColumnOrderChange?.(newOrdered);
      }
    };

    const EmptyState = () => (
      <div
        style={{
          padding: "40px 0",
          textAlign: "center",
          color: "#8c8c8c",
        }}
      >
        <i
          className={emptyIcon}
          style={{
            fontSize: 40,
            marginBottom: 12,
            display: "block",
            color: "#bfbfbf",
          }}
        />
        <div style={{ fontSize: 14 }}>{emptyText}</div>
      </div>
    );


    // Handle column resize
    const handleColumnResize = useCallback((columnKey: string, width: number) => {
      setColumnWidths((prev) => {
        const updated = { ...prev, [columnKey]: width };
        onColumnResize?.(columnKey, width);

        // Save to localStorage if storage key is provided
        if (columnWidthStorageKey) {
          try {
            localStorage.setItem(columnWidthStorageKey, JSON.stringify(updated));
          } catch (e) {
            console.warn('Failed to save column widths to localStorage', e);
          }
        }

        return updated;
      });
    }, [onColumnResize, columnWidthStorageKey]);

    // Prepare columns for rendering (inject onHeaderCell for drag and resize)
    const finalColumns = useMemo(() => {
      if (!enableColumnDrag && !enableColumnResize) {
        return orderedColumns;
      }

      return orderedColumns.map((col) => {
        // 🔥 IMPORTANT: skip selection column (checkbox column)
        if (!col.key && !col.dataIndex) {
          return col;
        }

        const columnKey = col.key || col.dataIndex;
        // Only override width if user has resized it, otherwise keep original
        const resizedWidth = columnWidths[columnKey];
        const columnConfig: any = {
          ...col,
          onHeaderCell: (column: any) => ({
            id: enableColumnDrag ? (column.key || column.dataIndex) : undefined,
            column,
            onResize: enableColumnResize ? handleColumnResize : undefined,
            columnWidths: enableColumnResize ? columnWidths : undefined,
          }),
        };

        // Only set width if user has resized it
        if (resizedWidth) {
          columnConfig.width = resizedWidth;
        }

        return columnConfig;
      });
    }, [orderedColumns, columnWidths, enableColumnDrag, enableColumnResize, handleColumnResize]);

    // Create header cell component that supports both drag and resize
    const HeaderCellComponent = enableColumnDrag
      ? DraggableHeaderCell
      : (enableColumnResize ? HeaderCellWithBackground : (components?.header?.cell || HeaderCellWithBackground));

    const tableComponents = {
      ...components,
      ...(draggable && { 
          body: { 
              row: (rowProps: any) => <SortableRow {...rowProps} CustomRow={(components?.body as any)?.row} /> 
          } 
      }),
      // Always apply header cell with background
      // Use DraggableHeaderCell if column drag is enabled, otherwise use HeaderCellWithBackground
      // If custom header.cell is provided, it will be overridden only if column drag or resize is enabled
      header: {
        ...(components?.header || {}),
        cell: (cellProps: any) => <HeaderCellComponent {...cellProps} CustomCell={components?.header?.cell} />,
      },
    };

    const table = (
      <Table
        className={className ?? "table datanew dataTable no-footer"}
        {...(finalRowSelection ? { rowSelection: finalRowSelection } : {})}

        columns={finalColumns}
        dataSource={dataToRender}
        rowKey={rowKey}
        loading={loading}
        locale={{
          emptyText: <EmptyState />,
        }}
        rowClassName={rowClassName}
        pagination={
          pagination && typeof pagination === "object"
            ? {
              ...pagination,

              showTotal: (total, range) => (
                <>
                  <span className="datatable-show-total">
                    Showing {range[0]} to {range[1]} of {total}{" "}
                    {total === 1 ? recordLabel : `${recordLabel}`}
                  </span>
                  <div className="d-flex align-items-start gap-2">


                    <input
                      type="number"
                      min={1}
                      value={pageSizeInput || ""}   // 👈 prevents showing 0
                      onChange={(e) => {
                        const value = Number(e.target.value);

                        // ❌ block 0, negatives, NaN
                        if (!value || value <= 0 || Number.isNaN(value)) {
                          setPageSizeInput(undefined);     // clear input instead of 0
                          return;
                        }

                        // ✅ valid number
                        setPageSizeInput(value);

                        // 🔥 live update
                        pagination.onChange?.(1, value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                        }
                      }}
                      style={{
                        width: 50,
                        height: 28,
                        fontSize: 13,
                        padding: "2px 6px",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                      }}
                    />

                  </div>
                </>
              ),
            }
            : pagination
        }



        scroll={scroll}
        components={tableComponents}
        expandable={expandable}
        {...rest}
      />
    );

    if (!draggable && !enableColumnDrag) return table;

    const rowItems = dataToRender.map(getRowId);
    // Filter out sticky columns from sortable context (they shouldn't be draggable)
    const columnItems = orderedColumns
      .filter((c) => !c.fixed) // Only include non-sticky columns
      .map((c) => c.key || c.dataIndex);

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columnItems}
          strategy={horizontalListSortingStrategy}
        >
          <SortableContext
            items={rowItems}
            strategy={verticalListSortingStrategy}
          >
            {table}
          </SortableContext>
        </SortableContext>

        {/* Visual Overlay for Dragging */}
        <DragOverlay>
          {activeDragItemId && (
            (() => {
              // 1. Column Drag
              const col = orderedColumns.find(c => (c.key || c.dataIndex) === activeDragItemId);
              if (col) {
                return (
                  <div
                    className="ant-table-cell" // Reuse antd table cell class for basic font/padding match
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      padding: "16px",
                      fontWeight: "bold",
                      borderRadius: "4px",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.15)",
                      cursor: "move",
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* If title is a function, we can't easily render it without props. If it's a node or string, we can. */}
                    {typeof col.title === "function" ? "Column" : col.title}
                  </div>
                );
              }

              // 2. Row Drag (Optional - currently rows handle their own dragging via useSortable transform, 
              // but if we wanted an overlay for rows too, we'd return it here. 
              // Since user said row drag is fine, we omit row overlay to keep behavior consistent with previous.)
              return null;
            })()
          )}
        </DragOverlay>
      </DndContext>
    );
  };

export default Datatable;
